import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import PickerModal from '../components/PickerModal';
import { LANGUAGES } from '../data/languages';
import { extractDocxText } from '../utils/docx';
import { LEGACY_FONTS, convertLegacyToUnicode, isLegacyFontSelected } from '../../utils/legacyFontConverter';
import { createDocxFile } from '../../utils/docxBuilder';
import '../mobile.css';

// Word extraction commonly leaves a blank line between paragraphs - strip
// those out so the review textarea shows compact text.
const removeEmptyLines = (text) =>
  (text || '')
    .split('\n')
    .filter((line) => line.trim() !== '')
    .join('\n');

const NewCaseDiary = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setFullFIR } = useCaseContext();
  const fileInputRef = useRef(null);

  const fir = location.state?.fir;
  const id = fir?.id;

  const [language, setLanguage] = useState('English');
  const [file, setFile] = useState(null);
  const [caseDiaryText, setCaseDiaryText] = useState('');
  const [legacyFont, setLegacyFont] = useState(LEGACY_FONTS[0]);
  // Holds the last raw (un-converted) case diary text - straight from the
  // Word file extraction, or from the user's typing/pasting in the review
  // box - so switching the Legacy Font picker re-converts from that
  // original source instead of re-running a conversion on text that may
  // already be converted once.
  const rawCaseDiaryTextRef = useRef('');
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [showLegacyFontPicker, setShowLegacyFontPicker] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFileSelected = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    setErrorMessage('');
    setFile(selected);
    setCaseDiaryText('');
    rawCaseDiaryTextRef.current = '';
    setIsParsingFile(true);

    try {
      const text = removeEmptyLines(await extractDocxText(selected));
      rawCaseDiaryTextRef.current = text;
      setCaseDiaryText(isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(text, legacyFont) : text);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to read the Word document.');
    } finally {
      setIsParsingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCaseDiaryTextChange = (e) => {
    setCaseDiaryText(e.target.value);
    rawCaseDiaryTextRef.current = e.target.value;
  };

  // Converts pasted text on the way in when a legacy font is selected - the
  // picker-driven reconversion only fires on a font *change*, so without
  // this a paste after the font was already selected would stay unconverted.
  const handleCaseDiaryTextPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (!pasted) return;
    e.preventDefault();

    const textarea = e.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const convertedPasted = isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(pasted, legacyFont) : pasted;
    const newDisplayed = caseDiaryText.slice(0, selectionStart) + convertedPasted + caseDiaryText.slice(selectionEnd);

    const raw = rawCaseDiaryTextRef.current;
    const rawStart = Math.min(selectionStart, raw.length);
    const rawEnd = Math.min(selectionEnd, raw.length);
    rawCaseDiaryTextRef.current = raw.slice(0, rawStart) + pasted + raw.slice(rawEnd);

    setCaseDiaryText(newDisplayed);

    const newCursorPos = selectionStart + convertedPasted.length;
    setTimeout(() => textarea.setSelectionRange(newCursorPos, newCursorPos), 0);
  };

  const isDisabled = loading || isParsingFile || !file || !caseDiaryText.trim();

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      // Upload a freshly-built .docx containing the (possibly legacy-font
      // converted, possibly user-edited) reviewed text, instead of the
      // originally-selected file.
      const docxFile = await createDocxFile(caseDiaryText, file.name);

      const form = new FormData();
      form.append('id', id);
      form.append('language', language);
      form.append('file', docxFile, docxFile.name);

      const api = createApiClient(API_URL);
      const response = await api.post('/fir/casediary', form);
      setFullFIR(response.data);
      navigate('/m/fir/chargesheet/generated', { state: { fir: response.data } });
    } catch {
      setErrorMessage('Failed to upload case diary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!id) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No case selected.</p>;

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#094268', marginBottom: '15px' }}>Upload Case Diary</h2>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Case Diary Language</label>
      <div
        onClick={() => setShowLanguagePicker(true)}
        style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' }}
      >
        <span>{language}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#094268', marginBottom: '6px' }}>Legacy Font</label>
      <div
        onClick={() => setShowLegacyFontPicker(true)}
        style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', cursor: 'pointer' }}
      >
        <span style={{ color: legacyFont !== LEGACY_FONTS[0] ? '#000' : '#999' }}>{legacyFont}</span>
        <span style={{ color: '#0A426A', fontWeight: 600 }}>Select</span>
      </div>

      <input ref={fileInputRef} type="file" accept=".docx" onChange={handleFileSelected} style={{ display: 'none' }} />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isParsingFile || loading}
        className="m-pill-btn"
        style={{ color: '#094268', fontWeight: 600, marginBottom: '12px' }}
      >
        {file ? file.name : 'Select Word Document'}
      </button>

      {file && (
        <div style={{ position: 'relative' }}>
          <textarea
            placeholder="Case diary content will appear here for review."
            value={caseDiaryText}
            onChange={handleCaseDiaryTextChange}
            onPaste={handleCaseDiaryTextPaste}
            disabled={isParsingFile}
            rows={10}
            style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontFamily: 'inherit', fontSize: '14px', opacity: isParsingFile ? 0.5 : 1 }}
          />

          {isParsingFile && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)' }}>
              <div style={{ textAlign: 'center' }}>
                <div className="m-spinner" style={{ margin: '0 auto', borderTopColor: '#094268' }} />
                <p style={{ marginTop: '8px', fontWeight: 600 }}>Reading Word document...</p>
              </div>
            </div>
          )}
        </div>
      )}

      <button onClick={handleSubmit} disabled={isDisabled} className="m-button-primary">
        {loading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Submit'}
      </button>

      {showLanguagePicker && (
        <PickerModal title="Language" options={LANGUAGES} selected={language} onSelect={setLanguage} onClose={() => setShowLanguagePicker(false)} />
      )}

      {showLegacyFontPicker && (
        <PickerModal
          title="Legacy Font"
          options={LEGACY_FONTS}
          selected={legacyFont}
          onSelect={(newFont) => {
            setLegacyFont(newFont);
            if (isLegacyFontSelected(newFont) && rawCaseDiaryTextRef.current.trim() !== '') {
              setCaseDiaryText(convertLegacyToUnicode(rawCaseDiaryTextRef.current, newFont));
            } else if (newFont === LEGACY_FONTS[0]) {
              // Not Applicable - restore the original text as first extracted/pasted, undoing any conversion.
              setCaseDiaryText(rawCaseDiaryTextRef.current);
            }
          }}
          onClose={() => setShowLegacyFontPicker(false)}
        />
      )}

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={() => setErrorMessage('')}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewCaseDiary;
